import { useBrand } from "@/contexts/BrandContext";

/**
 * BrandLoader — Loader oficial Touchworks (Vessel)
 *
 * Usa o s\u00edmbolo Vessel da marca com waves sutilmente animadas
 * e o texto "CARREGANDO" com pontos pulsando.
 *
 * Props:
 *  - fullscreen (bool) \u2014 ocupa toda a viewport (overlay com fundo s\u00f3lido)
 *  - inline (bool) \u2014 ocupa o container pai (h-full / w-full), sem overlay
 *  - label (string) \u2014 texto sob o logo (default: "CARREGANDO")
 *  - size (number) \u2014 largura do s\u00edmbolo em px (default: 200)
 *
 * EMB-16: no shell embutido, o logo do parceiro (BrandContext, T19)
 * substitui o s\u00edmbolo Vessel abaixo. Fora do embed, `logoUrl` \u00e9 sempre
 * `null` (padr\u00e3o do contexto), ent\u00e3o o Vessel de sempre continua id\u00eantico.
 */
export default function BrandLoader({
  fullscreen = false,
  inline = true,
  label = "CARREGANDO",
  size = 200,
}) {
  const { logoUrl } = useBrand();
  const wrapperClass = fullscreen
    ? "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background"
    : inline
      ? "flex flex-col items-center justify-center w-full h-full min-h-[200px] bg-transparent"
      : "flex flex-col items-center justify-center";

  return (
    <div className={wrapperClass} role="status" aria-label={label}>
      <style>{`
        @keyframes tw-wave-1 { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-2px) } }
        @keyframes tw-wave-2 { 0%,100% { transform: translateY(0) } 50% { transform: translateY(2px) } }
        @keyframes tw-wave-3 { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-1px) } }
        @keyframes tw-dots { 0%,20% { opacity: .25 } 50% { opacity: 1 } 80%,100% { opacity: .25 } }
        .tw-w1 { animation: tw-wave-1 2.4s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
        .tw-w2 { animation: tw-wave-2 2.4s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
        .tw-w3 { animation: tw-wave-3 2.4s ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
        .tw-dot { animation: tw-dots 1.4s ease-in-out infinite; }
        .tw-dot:nth-child(2) { animation-delay: .2s }
        .tw-dot:nth-child(3) { animation-delay: .4s }
      `}</style>

      {logoUrl ? (
        <img src={logoUrl} alt="" aria-hidden="true" style={{ width: size, height: "auto" }} />
      ) : (
      <svg
        viewBox="0 0 400 240"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: size, height: "auto" }}
        aria-hidden="true"
      >
        <defs>
          <clipPath id="tw-vessel-clip">
            <path d="M112,120 A88,88 0 0 1 288,120 Z" />
          </clipPath>
        </defs>

        {/* Vessel symbol */}
        <path
          d="M112,120 A88,88 0 0 1 288,120"
          stroke="currentColor"
          strokeWidth="5"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M128,120 A72,72 0 0 1 272,120"
          stroke="currentColor"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          opacity="0.4"
        />
        <line
          x1="104"
          y1="120"
          x2="296"
          y2="120"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
        />

        {/* Waves animadas */}
        <g clipPath="url(#tw-vessel-clip)">
          <path
            className="tw-w1"
            d="M125,98 Q150,82 175,98 Q200,114 225,98 Q250,82 275,98"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            opacity="0.95"
          />
          <path
            className="tw-w2"
            d="M125,110 Q150,94 175,110 Q200,126 225,110 Q250,94 275,110"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            opacity="0.65"
          />
          <path
            className="tw-w3"
            d="M125,120 Q150,104 175,120 Q200,136 225,120 Q250,104 275,120"
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            opacity="0.38"
          />
        </g>
      </svg>
      )}

      {label && (
        <div
          className="mt-4 flex items-center gap-2 text-xs tracking-[0.4em] text-foreground-muted/70"
          style={{ fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          <span>{label}</span>
          <span className="flex gap-1">
            <span className="tw-dot">.</span>
            <span className="tw-dot">.</span>
            <span className="tw-dot">.</span>
          </span>
        </div>
      )}

      <span className="sr-only">{label}</span>
    </div>
  );
}
